/**
 * The AI Service detail page's Analytics tab (task 1255; laid out to the design
 * file's stage 1 by task 1304).
 *
 * The questions a service owner asks over time: four stat tiles answer the
 * headline ones (runs, median duration, cost, error rate) against the
 * preceding period of the same length, and a two-column grid of charts shows
 * them day by day: Runs and Duration, Cost and Errors, then Criteria pass rate
 * full width. Every chart carries the service's publish and release events, so
 * a step in a series reads against the version that caused it, every day
 * drills down to the runs behind it in Run Explorer, and hovering a day on one
 * chart marks that day on all of them.
 *
 * One filter row scopes everything below it: the time range (presets or a
 * custom pair of dates, the last 30 days by default), the environment (all by
 * default) and whether tests are shown (off by default). The range, custom
 * dates and the tests switch live in the URL, so a reload or a shared link
 * keeps them. The series come from `GET /endpoints/:id/analytics` (task 1254),
 * which is account-scoped and caps a range at 90 days; the comparison period
 * is a second read of the same route.
 *
 * Test runs are their own category throughout: with "Include tests" on they are
 * a separate series on Runs and Cost, never summed into production totals,
 * tiles, Duration or Errors.
 *
 * The page renders this tab only while `innovate.fe.analytics-tab` is on, so
 * with the flag off the tab is not on the row and the series are never read.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useEndpointAnalytics } from '@/hooks/useEndpointAnalytics';
import { useDeploymentHistory } from '@/hooks/useEnvironments';
import type { Endpoint } from '@/types';
import type { Deployment } from '@/types/environments';
import { PolicySwitch } from '@/components/accountModels/PolicySwitch';
import { DailyChart } from './analytics/DailyChart';
import { StatTile } from './analytics/StatTile';
import {
  ANALYTICS_RANGES,
  DEFAULT_ANALYTICS_RANGE,
  comparisonRangeFor,
  formatCount,
  formatDay,
  formatDurationMs,
  formatPercent,
  formatUsd,
  hasNoRuns,
  isAnalyticsRangeKey,
  isoDay,
  percentDelta,
  periodPhrase,
  periodStatsOf,
  pointDelta,
  rangeFor,
  runsForDayLink,
  spanDays,
  totalsOf,
  validateCustomRange,
  type AnalyticsRangeKey,
} from './analytics/analyticsFormat';

interface ServiceAnalyticsTabProps {
  endpoint: Endpoint;
}

/** The environment filter's "all" value: the route is called without one. */
const ALL_ENVIRONMENTS = '';

/**
 * The environments this AI Service has been released to, in promotion order,
 * read off its rollout history. The filter value is what the route matches on.
 */
export function knownEnvironments(history: Deployment[] | undefined): { value: string; label: string }[] {
  const seen = new Map<string, { value: string; label: string; order: number }>();
  for (const row of history ?? []) {
    const env = row.environment;
    if (!env || seen.has(env.id)) continue;
    // The path: the environment's URL identity, unique per account, and one
    // of the three forms the route resolves (id, path or name).
    seen.set(env.id, { value: env.path, label: env.path, order: env.order });
  }
  return [...seen.values()].sort((a, b) => a.order - b.order).map(({ value, label }) => ({ value, label }));
}

/**
 * One line per criterion, one categorical slot each, never a cycled color: past
 * the eighth slot the chart says how many it leaves out.
 */
const MAX_CRITERIA_LINES = 8;

const selectClass =
  'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white';

/** The tab's own URL params: the range preset, a custom range's dates, and the tests switch. */
const PARAM = {
  range: 'range',
  from: 'from',
  to: 'to',
  tests: 'tests',
} as const;

const perDay = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

export function ServiceAnalyticsTab({ endpoint }: ServiceAnalyticsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get(PARAM.range);
  const rangeKey: AnalyticsRangeKey = isAnalyticsRangeKey(rangeParam) ? rangeParam : DEFAULT_ANALYTICS_RANGE;
  const customFrom = searchParams.get(PARAM.from) ?? '';
  const customTo = searchParams.get(PARAM.to) ?? '';
  const includeTests = searchParams.get(PARAM.tests) === '1';
  const [environment, setEnvironment] = useState<string>(ALL_ENVIRONMENTS);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Only a valid custom pair is ever written to the URL, so the range in force
  // is always the last good one; a hand-edited link that fails falls back to
  // the default preset.
  const { from, to } = useMemo(
    () => rangeFor(rangeKey, Date.now(), { from: customFrom, to: customTo }),
    [rangeKey, customFrom, customTo]
  );
  const comparison = useMemo(() => comparisonRangeFor(from, to), [from, to]);
  /** The custom inputs as typed, which may not be valid yet. */
  const [draft, setDraft] = useState<{ from: string; to: string }>(() =>
    rangeKey === 'custom' && customFrom && customTo ? { from: customFrom, to: customTo } : { from, to }
  );
  const draftError = rangeKey === 'custom' ? validateCustomRange(draft.from, draft.to) : null;

  const { data: history } = useDeploymentHistory(endpoint.id);
  const environments = useMemo(() => knownEnvironments(history), [history]);

  const { data, isLoading, error } = useEndpointAnalytics(endpoint.id, {
    from,
    to,
    environment: environment || undefined,
  });
  const previous = useEndpointAnalytics(endpoint.id, {
    from: comparison.from,
    to: comparison.to,
    environment: environment || undefined,
  });

  const writeParams = (update: (next: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        update(next);
        return next;
      },
      { replace: true }
    );

  const chooseRange = (key: AnalyticsRangeKey) =>
    writeParams((next) => {
      if (key === DEFAULT_ANALYTICS_RANGE) next.delete(PARAM.range);
      else next.set(PARAM.range, key);
      if (key === 'custom') {
        // Start the custom pair from the range in force, so switching to it
        // changes nothing until a date is changed.
        setDraft({ from, to });
        next.set(PARAM.from, from);
        next.set(PARAM.to, to);
      } else {
        next.delete(PARAM.from);
        next.delete(PARAM.to);
      }
    });

  const changeDraft = (field: 'from' | 'to', value: string) => {
    const nextDraft = { ...draft, [field]: value };
    setDraft(nextDraft);
    if (validateCustomRange(nextDraft.from, nextDraft.to) === null) {
      writeParams((next) => {
        next.set(PARAM.from, nextDraft.from);
        next.set(PARAM.to, nextDraft.to);
      });
    }
  };

  const toggleTests = () =>
    writeParams((next) => {
      if (includeTests) next.delete(PARAM.tests);
      else next.set(PARAM.tests, '1');
    });

  const days = data?.days ?? [];
  const dates = days.map((d) => d.date);
  const annotations = data?.annotations ?? [];
  const totals = totalsOf(days);
  const topErrors = data?.topErrors ?? [];
  const linkFor = (failedOnly: boolean) => (index: number) =>
    runsForDayLink({
      endpointId: endpoint.id,
      date: dates[index],
      environment: environment || undefined,
      failedOnly,
    });

  const stats = periodStatsOf(days);
  // Undefined while the comparison period loads (no delta yet), null when it
  // failed (a "—"), otherwise its figures.
  const before = previous.isLoading
    ? undefined
    : previous.error || !previous.data
      ? null
      : periodStatsOf(previous.data.days);
  const deltaOf = <T,>(compute: (prev: ReturnType<typeof periodStatsOf>) => T): T | null | undefined =>
    before === undefined ? undefined : before === null ? null : compute(before);
  const periodDays = spanDays(from, to);
  const deltaLabel = `vs previous ${periodDays === 1 ? 'day' : `${periodDays} days`}`;
  const hoverProps = { hoveredDay, onHover: setHoveredDay };

  return (
    <div className="space-y-4" data-testid="service-analytics-tab">
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-filters">
        <select
          aria-label="Time range"
          value={rangeKey}
          onChange={(e) => chooseRange(e.target.value as AnalyticsRangeKey)}
          className={selectClass}
        >
          {ANALYTICS_RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>
        {rangeKey === 'custom' && (
          <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="analytics-custom-range">
            <input
              type="date"
              aria-label="From"
              value={draft.from}
              max={isoDay(Date.now())}
              onChange={(e) => changeDraft('from', e.target.value)}
              className={selectClass}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">–</span>
            <input
              type="date"
              aria-label="To"
              value={draft.to}
              max={isoDay(Date.now())}
              onChange={(e) => changeDraft('to', e.target.value)}
              className={selectClass}
            />
          </span>
        )}
        <select
          aria-label="Environment"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          className={selectClass}
        >
          <option value={ALL_ENVIRONMENTS}>All environments</option>
          {environments.map((env) => (
            <option key={env.value} value={env.value}>
              {env.label}
            </option>
          ))}
        </select>
        <span className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <PolicySwitch
            checked={includeTests}
            onToggle={toggleTests}
            label="Include tests"
            testId="analytics-include-tests"
          />
          <span aria-hidden="true">Include tests</span>
        </span>
        {!previous.isLoading && (
          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400" data-testid="analytics-comparison-caption">
            {formatDay(from)} – {formatDay(to)} · compared with{' '}
            {previous.error ? '—' : `${formatDay(comparison.from)} – ${formatDay(comparison.to)}`}
          </span>
        )}
        {draftError && (
          <p
            role="alert"
            className="basis-full text-xs text-red-600 dark:text-red-400"
            data-testid="analytics-range-error"
          >
            {draftError}
          </p>
        )}
      </div>

      {isLoading ? (
        <div
          className="flex h-48 items-center justify-center text-sm text-gray-500 dark:text-gray-400"
          data-testid="analytics-loading"
        >
          Loading analytics…
        </div>
      ) : error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          data-testid="analytics-error"
        >
          Analytics could not be loaded. Please try again.
        </div>
      ) : !data || hasNoRuns(days) ? (
        <div
          className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600"
          data-testid="analytics-empty"
        >
          <ChartBarIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No runs {periodPhrase(rangeKey, from, to)}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Analytics fills in as this AI Service runs. Release it to an environment, or run a scenario from Testing and
            switch on Include tests.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="analytics-tiles">
            <StatTile
              testId="analytics-tile-runs"
              label="Runs"
              value={formatCount(stats.runs)}
              delta={deltaOf((prev) => percentDelta(stats.runs, prev.runs, true))}
              deltaLabel={deltaLabel}
              sub={`${perDay.format(stats.runsPerDay)} a day`}
              note={
                includeTests && totals.testRuns > 0
                  ? `plus ${formatCount(totals.testRuns)} ${totals.testRuns === 1 ? 'run' : 'runs'} from tests, counted apart`
                  : undefined
              }
              spark={days.map((d) => d.runs)}
            />
            <StatTile
              testId="analytics-tile-duration"
              label="Median duration"
              value={stats.medianMs === null ? '—' : formatDurationMs(stats.medianMs)}
              delta={deltaOf((prev) => percentDelta(stats.medianMs, prev.medianMs, false))}
              deltaLabel={deltaLabel}
              sub={`slowest tenth ${stats.p90Ms === null ? '—' : formatDurationMs(stats.p90Ms)}`}
              spark={days.map((d) => d.duration.median)}
            />
            <StatTile
              testId="analytics-tile-cost"
              label="Cost"
              value={formatUsd(stats.costUsd)}
              delta={deltaOf((prev) => percentDelta(stats.costUsd, prev.costUsd, false))}
              deltaLabel={deltaLabel}
              sub={`${stats.costPerRun === null ? '—' : formatUsd(stats.costPerRun)} a run`}
              spark={days.map((d) => d.costUsd ?? 0)}
            />
            <StatTile
              testId="analytics-tile-errors"
              label="Error rate"
              value={stats.errorRate === null ? '—' : formatPercent(stats.errorRate)}
              delta={deltaOf((prev) => pointDelta(stats.errorRate, prev.errorRate))}
              deltaLabel={deltaLabel}
              sub={`${formatCount(stats.failedRuns)} failed ${stats.failedRuns === 1 ? 'run' : 'runs'}`}
              spark={days.map((d) => d.errorRate)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="analytics-charts">
            <DailyChart
              title="Runs"
              subtitle={includeTests ? 'Runs per day; runs from tests shown apart' : 'Production runs per day'}
              testId="analytics-runs-chart"
              kind="bar"
              dates={dates}
              annotations={annotations}
              series={[
                {
                  key: 'runs',
                  label: 'Runs',
                  slot: 0,
                  values: days.map((d) => d.runs),
                },
                ...(includeTests
                  ? [
                      {
                        key: 'testRuns',
                        label: 'Tests',
                        slot: 2,
                        values: days.map((d) => d.testRuns),
                      },
                    ]
                  : []),
              ]}
              formatValue={formatCount}
              integerAxis
              dayLink={linkFor(false)}
              {...hoverProps}
            />

            <DailyChart
              title="Duration"
              subtitle="Per run, per day, over runs that did not fail"
              testId="analytics-duration-chart"
              kind="line"
              dates={dates}
              annotations={annotations}
              series={[
                {
                  key: 'median',
                  label: 'Median',
                  slot: 0,
                  values: days.map((d) => d.duration.median),
                },
                {
                  key: 'p90',
                  label: 'Slowest tenth (p90)',
                  slot: 1,
                  values: days.map((d) => d.duration.p90),
                },
              ]}
              formatValue={formatDurationMs}
              dayLink={linkFor(false)}
              {...hoverProps}
            />

            <DailyChart
              title="Cost"
              subtitle={`Estimated, from token usage priced by the Model catalog · ${formatUsd(totals.costUsd)} and ${formatCount(totals.tokens)} tokens in this period`}
              testId="analytics-cost-chart"
              kind="bar"
              dates={dates}
              annotations={annotations}
              series={[
                {
                  key: 'costUsd',
                  label: includeTests ? 'Production' : 'Estimated cost',
                  slot: 0,
                  values: days.map((d) => d.costUsd),
                },
                ...(includeTests
                  ? [
                      {
                        key: 'testCostUsd',
                        label: 'Tests',
                        slot: 2,
                        values: days.map((d) => d.testCostUsd ?? null),
                      },
                    ]
                  : []),
              ]}
              formatValue={formatUsd}
              readoutExtra={(i) => (
                <div className="mt-0.5 flex justify-between gap-3 text-gray-600 dark:text-gray-300">
                  <span>Tokens</span>
                  <span className="font-medium text-gray-900 tabular-nums dark:text-gray-100">
                    {formatCount(days[i].tokens)}
                  </span>
                </div>
              )}
              tableExtra={{
                header: 'Tokens',
                cell: (i) => formatCount(days[i].tokens),
              }}
              dayLink={linkFor(false)}
              {...hoverProps}
            />
            <DailyChart
              title="Errors"
              subtitle="Failed runs as a share of the day's runs"
              testId="analytics-errors-chart"
              kind="line"
              dates={dates}
              annotations={annotations}
              series={[
                {
                  key: 'errorRate',
                  label: 'Error rate',
                  slot: 0,
                  values: days.map((d) => d.errorRate),
                },
              ]}
              formatValue={formatPercent}
              yMax={Math.max(...days.map((d) => d.errorRate ?? 0)) > 0.5 ? 1 : undefined}
              dayLink={linkFor(true)}
              {...hoverProps}
            >
              <div
                className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700"
                data-testid="analytics-top-errors"
              >
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Top error messages
                </h4>
                {topErrors.length === 0 ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No failed runs in this period.</p>
                ) : (
                  <ul className="mt-1 space-y-1">
                    {topErrors.map((err) => (
                      <li key={err.message} className="flex gap-3 text-xs text-gray-700 dark:text-gray-300">
                        <span className="w-8 shrink-0 text-right font-medium tabular-nums text-gray-900 dark:text-gray-100">
                          {formatCount(err.count)}
                        </span>
                        <span className="break-words">{err.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </DailyChart>

            <div className="lg:col-span-2">
              {data.criteriaPassRate.length > 0 ? (
                <DailyChart
                  title="Criteria pass rate"
                  subtitle={`Share of scored results that passed, per criterion, per day${
                    data.criteriaPassRate.length > MAX_CRITERIA_LINES
                      ? ` · first ${MAX_CRITERIA_LINES} of ${data.criteriaPassRate.length} criteria`
                      : ''
                  }`}
                  testId="analytics-criteria-chart"
                  kind="line"
                  dates={dates}
                  annotations={annotations}
                  series={data.criteriaPassRate.slice(0, MAX_CRITERIA_LINES).map((criterion, index) => {
                    const byDate = new Map(criterion.days.map((d) => [d.date, d.passRate]));
                    return {
                      key: criterion.criterionId,
                      label: criterion.name,
                      slot: index,
                      values: dates.map((date) => byDate.get(date) ?? null),
                    };
                  })}
                  formatValue={formatPercent}
                  yMax={1}
                  connectGaps
                  dayLink={linkFor(false)}
                  {...hoverProps}
                />
              ) : (
                <section
                  data-testid="analytics-criteria-chart"
                  className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                >
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Criteria pass rate</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    No criteria were scored in this period. Run tests from the Testing tab to see pass rates here.
                  </p>
                </section>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ServiceAnalyticsTab;
